import { useDispatch, useSelector } from 'react-redux';
import { DownloadDocumentIcon, UploadDocumentIcon } from '../../assets';
import { useLazyGetDownloadDocumentQuery, useLazyGetCompanyDetailsQuery, usePostIPDocumentMutation } from '../../api';
import { docDownload } from '../../utils';
import { showToast } from '../../redux/slices';
import { showDocFileName, validateUUID } from '../../helpers';
import { ChangeEvent, useEffect, useState } from 'react';
import { RootState } from '../../redux/store';
import { Loader, NoRecordsFound } from '../../components';
import { FILE_EXTENSION_TYPES, PDF_FILE_SIZE_VALIDATIONS, FILE_TYPES, DOCUMENT_SIZE_NOTE, IP_WHITELISTING_DOCUMENTS } from '../../data';

interface ISingleDocuments {
    ipDocName: string;
    label: string;
    description: string;
    docFieldName: string;
}

const IPWhitelistingDocumentsTable = () => {
    const dispatch = useDispatch();
    const [getDocumentDownload] = useLazyGetDownloadDocumentQuery();
    const [uploadedIPDocId, setuploadedIPDocId] = useState<string | null>("");
    const [getCompanyDetails, { isLoading: isGetCompanyDetailsLoading, data: companyDetails }] = useLazyGetCompanyDetailsQuery();
    const [postIpDocument, { isLoading: isPostIpDocumentLoading }] = usePostIPDocumentMutation();
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);
    const tableHeaders = ["No.", "Name", "Description", "Upload Document", "Download Document"];
    const bank = useSelector((state: RootState) => state.auth.bank);
    const isMarkCompleted = bank?.markCompleted === true;

    /* DOWNLOAD DOCUMENT */
    const handleDownload = (id: string | null, fileName: string) => {
        if (!id) {
            dispatch(showToast({ message: "Document not uploaded yet.", type: 'error' }));
            return
        }

        /* CHECK VALID UUID */
        if (!validateUUID(id)) {
            dispatch(showToast({ message: "Invalid Document!", type: 'error' }));
            return;
        }
        setIsDownloadLoading(true)
        /* DOWNLOAD DOCUMENT */
        getDocumentDownload(id)
            .unwrap()
            .then((res) => {
                if (res) {
                    docDownload(res, fileName);
                    dispatch(showToast({ message: "File Downloaded Successfully.", type: 'success' }));
                    setIsDownloadLoading(false);
                }
            })
            .catch((error) => {
                dispatch(showToast({ message: error?.data?.message || "Failed", type: 'error' }));
                setIsDownloadLoading(false)
            });
    }

    /* HANDLE FILE UPLOAD */
    const handleFileChange = (ipDocName: string, event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== FILE_TYPES.PDF) {
            dispatch(showToast({ message: "Invalid file type. Only PDF files are allowed.", type: 'error' }));
            return;
        }

        if (file.size < PDF_FILE_SIZE_VALIDATIONS.MINIMUM_SIZE) { // 30 KB
            dispatch(showToast({ message: `File size must be at least ${PDF_FILE_SIZE_VALIDATIONS.MINIMUM_SIZE / 1024} KB!`, type: 'error' }));
            return;
        }

        if (file.size > PDF_FILE_SIZE_VALIDATIONS.MAXIMUM_SIZE) { // 100 KB
            dispatch(showToast({ message: `File size cannot exceed ${PDF_FILE_SIZE_VALIDATIONS.MAXIMUM_SIZE / 1024 / 1024} MB!`, type: 'error' }));
            return;
        }
        setuploadedIPDocId(ipDocName);
        /* SUBMIT FORM */
        const payload = new FormData();
        payload.append('ipDoc', file);
        payload.append('bankId', bank?.id || '');
        payload.append('ipDocName', ipDocName);

        postIpDocument(payload)
            .unwrap()
            .then((res) => {
                if (res) {
                    dispatch(showToast({ message: res?.message || "Success!", type: 'success' }));
                    getCompanyDetails(bank?.id).unwrap();
                }
            })
            .catch((error) => dispatch(showToast({ message: error?.data?.message || "Failed", type: 'error' })))
            .finally(() => setuploadedIPDocId(null));
    };

    useEffect(() => {
        /* COMPANY DETAILS */
        getCompanyDetails(bank?.id).unwrap();
    }, [bank?.id, getCompanyDetails])

    return (
        <section className="upload-lending-journey-docuement-section">
            <div className="table-box">
                <table className="table table-responsive common-table">
                    <thead>
                        <tr>
                            {tableHeaders.map((header, index) => (
                                <th key={index}>{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isGetCompanyDetailsLoading
                            ? <tr>
                                <td colSpan={tableHeaders.length}><Loader height="400px" /></td>
                            </tr>
                            : IP_WHITELISTING_DOCUMENTS?.length > 0
                                ? IP_WHITELISTING_DOCUMENTS?.map((ipDoc: ISingleDocuments, index: number) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{ipDoc?.label}</td>
                                        <td>{ipDoc?.description}</td>
                                        <td>
                                            <label className={`upload-btn ${isPostIpDocumentLoading || isMarkCompleted ? 'disabled' : ''}`}>
                                                <img src={UploadDocumentIcon} alt="Icon" />
                                                {isPostIpDocumentLoading && ipDoc?.ipDocName === uploadedIPDocId
                                                    ? <Loader height="10px" />
                                                    : <p className="">
                                                        {companyDetails?.body?.[ipDoc?.docFieldName] ? showDocFileName(ipDoc.label, `${ipDoc?.label}.pdf`) : 'Upload File'}
                                                    </p>
                                                }
                                                <input
                                                    type="file"
                                                    id={ipDoc?.ipDocName}
                                                    name={ipDoc?.ipDocName}
                                                    onChange={(e) => handleFileChange(ipDoc?.ipDocName, e)}
                                                    accept={FILE_EXTENSION_TYPES.PDF}
                                                    disabled={isPostIpDocumentLoading || isMarkCompleted}
                                                />
                                            </label>
                                        </td>
                                        <td>
                                            {companyDetails?.body?.[ipDoc?.docFieldName]
                                                ? (<p className="btn light-btn m-0" onClick={() => handleDownload(companyDetails?.body?.[ipDoc?.docFieldName], `${ipDoc?.label}.pdf`)}>
                                                    {isDownloadLoading && <div className="downloading-button-loader" />}
                                                    {!isDownloadLoading && <img src={DownloadDocumentIcon} alt="Download Document" />}
                                                    Download
                                                </p>)
                                                : (<p className="pink-text">Not Uploaded!</p>)
                                            }
                                        </td>
                                    </tr>
                                ))
                                : <tr>
                                    <td colSpan={tableHeaders.length}><NoRecordsFound height="100px" message="No Documents Available!" /></td>
                                </tr>
                        }
                        <tr className="table-spacer-row m-0 p-0">
                            <td colSpan={tableHeaders.length} />
                        </tr>
                    </tbody>
                </table>
                <p className="main-btn-group note">{DOCUMENT_SIZE_NOTE}</p>
            </div>
        </section>
    )
}

export default IPWhitelistingDocumentsTable;
